/**
 * 문장에서 **테이블 참조와 별칭**을 뽑는다 — 컬럼 자동완성이 `a.` 뒤에 무엇을 보여 줄지 정하는 근거(2026-10-03).
 *
 * 정식 파서가 아니다. FROM · JOIN · UPDATE · INSERT INTO · DELETE FROM · MERGE INTO/USING 뒤의
 * `[스키마.]테이블 [AS] [별칭]` 꼴을 쉼표 단위로 읽는다. 서브쿼리 `( ... )` 는 건너뛴다(괄호 안의 FROM 은 그
 * 서브쿼리의 것이고 별칭은 바깥에 붙는다 — 그 별칭은 테이블이 아니라 컬럼을 알 수 없으니 뺀다).
 * 틀리면 자동완성 후보가 덜 나올 뿐 실행과 무관하다. 순수 함수 — 테스트가 고정한다.
 */
import { stripSqlComments } from './statementAtCursor';

const KEYWORDS_END_LIST = new Set([
    'WHERE', 'ON', 'GROUP', 'ORDER', 'HAVING', 'UNION', 'MINUS', 'INTERSECT', 'EXCEPT', 'SET', 'VALUES', 'SELECT',
    'LIMIT', 'FETCH', 'OFFSET', 'CONNECT', 'START', 'WHEN', 'USING', 'RETURNING', 'FOR', 'WITH', 'WINDOW', 'QUALIFY', 'MODEL', 'PIVOT', 'UNPIVOT',
]);
const JOIN_WORDS = new Set(['JOIN', 'INNER', 'LEFT', 'RIGHT', 'FULL', 'CROSS', 'NATURAL', 'OUTER']);
// 식별자 — 한글 테이블명(교육DB 의 `고객거래요약_STG`, 화면 검증 2026-10-03)처럼 ASCII 가 아닌 글자도 받는다(\p{L}·\p{N}, u 플래그)
const IDENT = /^[\p{L}_][\p{L}\p{N}_$#]*$|^"[^"]+"$/u;

function tokenize(text) {
    const out = [];
    const re = /"[^"]*"|'(?:[^']|'')*'|[\p{L}_][\p{L}\p{N}_$#]*|\.|,|\(|\)|[^\s]/gu;
    let m;
    while ((m = re.exec(text)) !== null) out.push(m[0]);
    return out;
}

const unquote = (s) => (s.startsWith('"') ? s.slice(1, -1) : s.toUpperCase());

/**
 * @param {string} statementText 한 문장
 * @returns {Array<{ schema: string|null, table: string, alias: string|null }>} 등장 순서. 이름은 대문자(따옴표면 그대로)
 */
export function parseTableRefs(statementText) {
    const toks = tokenize(stripSqlComments(String(statementText || '')));
    const refs = [];
    const seen = new Set();
    const push = (schema, table, alias) => {
        const key = `${schema || ''}.${table}.${alias || ''}`;
        if (seen.has(key)) return;
        seen.add(key);
        refs.push({ schema, table, alias });
    };
    const skipParen = (i) => {           // toks[i] === '(' → 짝 뒤 index
        let depth = 0;
        for (; i < toks.length; i++) {
            if (toks[i] === '(') depth++;
            else if (toks[i] === ')') { depth--; if (depth === 0) return i + 1; }
        }
        return toks.length;
    };
    // 이름 하나 읽기: ident(.ident)? [AS] [alias]
    const readRef = (i) => {
        if (i >= toks.length || !IDENT.test(toks[i])) return { i, ref: null };
        // MERGE ... WHEN MATCHED THEN UPDATE SET — UPDATE 뒤가 키워드면 테이블이 아니다
        if (KEYWORDS_END_LIST.has(toks[i].toUpperCase()) || JOIN_WORDS.has(toks[i].toUpperCase())) return { i, ref: null };
        let schema = null;
        let table = unquote(toks[i]);
        i++;
        if (toks[i] === '.' && i + 1 < toks.length && IDENT.test(toks[i + 1])) {
            schema = table; table = unquote(toks[i + 1]); i += 2;
        }
        let alias = null;
        if (i < toks.length && toks[i].toUpperCase() === 'AS') i++;
        if (i < toks.length && IDENT.test(toks[i])) {
            const up = toks[i].toUpperCase();
            if (!KEYWORDS_END_LIST.has(up) && !JOIN_WORDS.has(up) && up !== 'AS') { alias = unquote(toks[i]); i++; }
        }
        return { i, ref: { schema, table, alias } };
    };
    const readList = (i) => {            // FROM 뒤: ref (, ref)* — 서브쿼리는 건너뛴다
        for (;;) {
            if (toks[i] === '(') {
                i = skipParen(i);
                // 서브쿼리의 별칭은 건너뛴다
                if (i < toks.length && toks[i].toUpperCase() === 'AS') i++;
                if (i < toks.length && IDENT.test(toks[i]) && !KEYWORDS_END_LIST.has(toks[i].toUpperCase()) && !JOIN_WORDS.has(toks[i].toUpperCase())) i++;
            } else {
                const r = readRef(i);
                if (!r.ref) return i;
                push(r.ref.schema, r.ref.table, r.ref.alias);
                i = r.i;
            }
            if (toks[i] === ',') { i++; continue; }
            return i;
        }
    };
    for (let i = 0; i < toks.length; i++) {
        const up = toks[i].toUpperCase();
        if (up === 'FROM' || up === 'JOIN' || up === 'INTO' || up === 'UPDATE' || up === 'USING') {
            // DELETE FROM t / INSERT INTO t / MERGE INTO t USING s — 전부 같은 꼴
            i = readList(i + 1) - 1;
        }
    }
    return refs;
}

/**
 * 별칭(또는 테이블 이름)으로 참조를 찾는다. `a` → { table: 'EMP' } 처럼. 별칭이 없는 참조는 테이블 이름이 별칭 노릇을 한다.
 */
export function resolveRef(refs, token) {
    const up = String(token || '').toUpperCase();
    if (!up) return null;
    return refs.find(r => (r.alias || '').toUpperCase() === up)
        || refs.find(r => r.table.toUpperCase() === up)
        || null;
}
